import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getBiddingDetails,
  updateBidding,
} from "sr/utils/api/corporate/biddings";
import { Bidding } from "sr/models/Bidding";
import { toast } from "react-toastify";
import { RequirementCommonHeader } from "../layout/RequirementCommonHeader";
import DropDown from "sr/helpers/ui-components/DropDown.tsx";
import { StatusPill } from 'sr/helpers/ui-components/StatusPill'
import ContentWithLoader from 'sr/layout/common/ContentLoader'
import { MetaBox } from 'sr/helpers/ui-components/MetaBox'
import AttachmentList from "sr/helpers/ui-components/AttachmentList";
import { useIntl } from 'react-intl'
import { getPricingType } from 'sr/utils/api/masterData/getPricingType'
import { getPaymentTerms } from 'sr/utils/api/masterData/getPaymentTerms'

export const BiddingDetails = () => {
  const intl = useIntl();
  const [pricingLabelMap, setPricingLabelMap] = useState<Record<string, string>>({});
  const [termsLabelMap, setTermsLabelMap] = useState<Record<string, string>>({});
  useEffect(() => {
    const load = async () => {
      try {
        const p = await getPricingType();
        if (p?.success && Array.isArray(p.data)) {
          const m: Record<string, string> = {};
          p.data.forEach((i: any) => (m[i.value] = i.label));
          setPricingLabelMap(m);
        }
      } catch {}
      try {
        const t = await getPaymentTerms();
        if (t?.success && Array.isArray(t.data)) {
          const m: Record<string, string> = {};
          t.data.forEach((i: any) => (m[i.value] = i.label));
          setTermsLabelMap(m);
        }
      } catch {}
    };
    load();
  }, []);
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [bidding, setBidding] = useState<Bidding | null>(null);

  const [status, setStatus] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [savingStatus, setSavingStatus] = useState<boolean>(false);

  const fetchDetails = async () => {
    if (!id) return;
    setLoading(true);
    const res = await getBiddingDetails(id);
    if (res && res.success) {
      const data = res.data as Bidding;
      setBidding(data);
      const s = String((data as any)?.status || "");
      setStatus(s);
      setSelectedStatus(s);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const handleStatusChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (!id || !bidding) return;
    const newStatus = e.target.value;
    setSelectedStatus(newStatus);
    setSavingStatus(true);

    try {
      const b: any = bidding;
      const payload: any = {
        corporate_id: String(b?.corporate_id?._id || ''),
        company_id: String(b?.company_id?._id || ''),
        requirement_id: String(b?.requirement_id?._id || ''),
        description: b?.description,
        experience: b?.experience,
        documents: b?.documents,
        pricing_type: b?.pricing_type,
        payment_terms: b?.payment_terms,
        bid_amount: b?.bid_amount,
        status: newStatus,
      };

      const res = await updateBidding(String(id), payload);

      if (res && (res as any).success) {
        setStatus(newStatus);
        setBidding({ ...(bidding as any), status: newStatus });
        toast.success("Status updated");
      } else {
        toast.error("Failed to update status");
        setSelectedStatus(status);
      }
    } catch (err) {
      toast.error("Failed to update status");
      setSelectedStatus(status);
    } finally {
      setSavingStatus(false);
    }
  };

  const getReqLabel = (b: any) =>
    b?.requirement_id?.title ||
    "—";
  const getCompanyLabel = (b: any) =>
    b?.company_id?.company_name ||
    "—";

  const SkeletonCard = () => (
    <div className="p-6 flex flex-col gap-6 rounded-[20px] mb-6 border border-2xl animate-pulse">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="h-6 w-32 bg-gray-200 rounded"></div>
          <div className="h-5 w-20 bg-gray-200 rounded-full"></div>
          <div className="h-5 w-16 bg-gray-200 rounded-full"></div>
        </div>
        <div className="h-11 w-56 bg-gray-200 rounded-lg"></div>
      </div>
      <div className="h-4 w-2/3 bg-gray-200 rounded"></div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-200 rounded-lg"></div>
        ))}
      </div>
      <div className="h-4 w-1/4 bg-gray-200 rounded"></div>
    </div>
  );

  return (
    <div className="py-3">
      <RequirementCommonHeader title="Bidding Details" />

      <ContentWithLoader loading={loading} content={<SkeletonCard />}>
        {!bidding ? (
          <div className="p-6">No details found.</div>
        ) : (
          <div className="p-6 flex flex-col gap-6 rounded-[20px] mb-6 border border-2xl">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex gap-4">
                <div className="flex flex-col gap-1">
                  <div className="flex gap-4">
                    <h3 className="text-lg font-semibold text-[#0078ae]">
                      {String(getReqLabel(bidding))}
                    </h3>
                    <div>
                      <StatusPill status={status} />
                    </div>
                  </div>

                  {getCompanyLabel(bidding) && (
                    <div className="text-xs text-gray-600 flex items-center gap-1 mt-1">
                      <span className="font-medium">{String(getCompanyLabel(bidding))}</span>
                    </div>
                  )}
                </div>
              </div>

              {status !== "withdrawn" ? <div className="w-[200px]">
                <DropDown
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  disabled={savingStatus}
                  className="border border-gray-300 rounded-lg h-11"
                  dropdowndiv="mt-0"
                >
                  <option value="">-- Select --</option>
                  <option value="pending">pending</option>
                  <option value="accepted">accepted</option>
                  <option value="shortlisted">shortlisted</option>
                  <option value="rejected">rejected</option>
                  <option value="withdrawn">withdrawn</option>
                </DropDown>
                {savingStatus && (
                  <p className="text-xs text-gray-500 mt-1">Updating...</p>
                )}
              </div>
                : null}
            </div>

            {bidding.description && (
              <p className="text-sm text-gray-600">{bidding.description}</p>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <MetaBox label="Amount" value={String(bidding.bid_amount ?? "—")} />
              <MetaBox label="Pricing" value={
                bidding?.pricing_type
                  ? intl.formatMessage({ id: pricingLabelMap[String(bidding.pricing_type)] || String(bidding.pricing_type), defaultMessage: String(bidding.pricing_type) })
                  : '—'
              } />
              <MetaBox label="Payment Terms" value={
                bidding?.payment_terms
                  ? intl.formatMessage({ id: termsLabelMap[String(bidding.payment_terms)] || String(bidding.payment_terms), defaultMessage: String(bidding.payment_terms) })
                  : '—'
              } />
              <MetaBox
                label="Created"
                value={
                  bidding.createdAt
                    ? new Date(bidding.createdAt).toLocaleDateString()
                    : "—"
                }
              />
              <MetaBox
                label="Updated"
                value={
                  bidding.updatedAt
                    ? new Date(bidding.updatedAt).toLocaleDateString()
                    : "—"
                }
              />
            </div>

            {Array.isArray((bidding as any).documents) &&
              (bidding as any).documents.length > 0 && (
                <AttachmentList items={(bidding as any).documents || []} />
              )}
          </div>
        )}
      </ContentWithLoader>
    </div>
  );
};

export default BiddingDetails;
